/**
 * Copy for backend-generated emails. The renderer under
 * `apps/backend/src/modules/communications/.../renderers/<area>/` calls
 * `t('emails.<area>.<key>', params)`; an ESLint rule forbids inline
 * string literals in those files, so this dictionary is the only
 * source of email copy.
 *
 * Sign-offs reuse `common['sign-off']` rather than restating it here
 * — keep brand voice in one place.
 */
export const emails = {
  'member-invite': {
    subject: 'Willkommen bei {workspaceName} – richte dein Profil ein',
    title: 'Willkommen bei {workspaceName}',
    greeting: 'Hallo {firstName},',
    'greeting-fallback': 'Hallo,',
    body:
      '{greeting}\n' +
      '\n' +
      '{inviterName} hat dich zu {workspaceName} eingeladen. Richte in wenigen Minuten dein Profil ein — Foto, Position und Kontaktdaten —, damit du direkt loslegen kannst und im Exposé sowie auf deinem Makler-Profil richtig erscheinst.',
    'cta-label': 'Onboarding starten',
    footnote:
      'Der Link ist 14 Tage gültig und nur für dich bestimmt. Du wurdest nicht erwartet? Dann ignoriere diese E-Mail einfach.',
  },
  'new-lead': {
    subject: 'Neuer Lead: {lastName} ({location})',
    'subject-no-location': 'Neuer Lead: {lastName}',
    title: 'Neuer Eigentümer-Lead',
    greeting: 'Hallo {firstName},',
    'greeting-fallback': 'Hallo,',
    intro:
      'dir wurde ein neuer Eigentümer-Lead zugewiesen. Du kannst {ownerFirstName} direkt kontaktieren — die wichtigsten Daten stehen unten.',
    'intro-no-name': 'dir wurde ein neuer Eigentümer-Lead zugewiesen. Die wichtigsten Daten stehen unten.',
    // Zur Kenntnis an Inhaber:innen / Admins: der Lead ist ihnen nicht zugewiesen,
    // deshalb nie „zugewiesen" behaupten.
    'intro-cc': 'in eurem Workspace ist ein neuer Eigentümer-Lead eingegangen. Die wichtigsten Daten stehen unten.',
    'group-contact': 'Eigentümer-Kontakt',
    'line-name': 'Name: {name}',
    'line-phone': 'Telefon: {phone}',
    'line-email': 'E-Mail: {email}',
    'group-object': 'Objekt',
    'line-address': 'Adresse: {address}',
    'line-object-type': 'Objektart: {type}',
    'eckdaten-both': 'Eckdaten: {area} m² · Baujahr {year}',
    'eckdaten-area': 'Eckdaten: {area} m²',
    'eckdaten-year': 'Eckdaten: Baujahr {year}',
    'line-source': 'Quelle: {source}',
    'line-standort': 'Standort: {standort}',
    'standort-none': 'kein Standort',
    'group-note': 'Notiz',
    'cta-label': 'Lead öffnen',
    'fallback-note':
      'Dieser Lead konnte keiner Standortleitung zugeordnet werden und wurde dir als Workspace-Inhaber:in zugewiesen.',
    'cc-note': 'Du erhältst diese E-Mail, weil du Inhaber:in oder Admin dieses Workspace bist.',
    'source-label':
      '{source, select, MANUAL {Manuell erfasst} PARTNER {Partner} PORTAL {Portal} WEBSITE {Website} REFERRAL {Empfehlung} COLD_OUTREACH {Kaltakquise} other {Sonstiges}}',
  },
  'lead-qualified': {
    subject: 'Lead qualifiziert: {lastName} ({location})',
    'subject-no-location': 'Lead qualifiziert: {lastName}',
    title: 'Lead qualifiziert',
    greeting: 'Hallo {firstName},',
    'greeting-fallback': 'Hallo,',
    intro:
      '{actorName} hat am {date} um {time} Uhr einen Eigentümer-Lead qualifiziert. Daraus sind ein Kontakt, ein Objekt und ein Deal entstanden.',
    'intro-fallback':
      'Ein Teammitglied hat am {date} um {time} Uhr einen Eigentümer-Lead qualifiziert. Daraus sind ein Kontakt, ein Objekt und ein Deal entstanden.',
    'group-contact': 'Eigentümer-Kontakt',
    'line-name': 'Name: {name}',
    'line-phone': 'Telefon: {phone}',
    'line-email': 'E-Mail: {email}',
    'group-object': 'Objekt',
    'line-address': 'Adresse: {address}',
    'line-object-type': 'Objektart: {type}',
    'eckdaten-both': 'Eckdaten: {area} m² · Baujahr {year}',
    'eckdaten-area': 'Eckdaten: {area} m²',
    'eckdaten-year': 'Eckdaten: Baujahr {year}',
    'group-note': 'Notiz',
    'line-source': 'Quelle: {source}',
    'line-standort': 'Standort: {standort}',
    'line-startphase': 'Startphase: {phase}',
    'cta-label': 'Deal öffnen',
    'source-label':
      '{source, select, MANUAL {Manuell erfasst} PARTNER {Partner} PORTAL {Portal} WEBSITE {Website} REFERRAL {Empfehlung} COLD_OUTREACH {Kaltakquise} other {Sonstiges}}',
    'startphase-label':
      '{phase, select, IN_EXCHANGE {Im Austausch} APPOINTMENT_SCHEDULED {Termin vereinbart} other {Im Austausch}}',
  },
  'valuation-completed': {
    subject: 'Bewertung fertig: {address}',
    'subject-no-address': 'Bewertung fertig',
    title: 'Bewertung fertig',
    greeting: 'Hallo {firstName},',
    'greeting-fallback': 'Hallo,',
    intro: 'die Bewertung ist da. Hier das Ergebnis:',
    'group-result': 'Ergebnis',
    'line-price-range': 'Preisspanne: {from} – {to}',
    'line-recommended': 'Empfohlener Startpreis: {price}',
    'group-object': 'Objekt',
    'line-address': 'Adresse: {address}',
    'line-object-type': 'Objektart: {type}',
    'line-usage': 'Nutzung: {usage}',
    'cta-label': 'Bewertung öffnen',
    'objectType-label':
      '{type, select, CONDOMINIUM {Eigentumswohnung} SINGLE_FAMILY_TERRACED_HOUSE {Einfamilien-/Reihenhaus} MULTI_FAMILY_HOUSE {Mehrfamilienhaus} PLOT {Grundstück} COMMERCIAL_PROPERTY {Gewerbeobjekt} GARAGE_PARKING_SPACE {Garage/Stellplatz} other {Objekt}}',
    'usage-label':
      '{usage, select, OWNER_OCCUPIED {Selbstgenutzt} RENTED {Vermietet} PARTIALLY_RENTED {Teilvermietet} VACANT {Leerstehend} other {–}}',
  },
  'partner-onboarding-invite': {
    subject: 'Einladung zum Onboarding für „{organisationName}"',
    title: 'Onboarding für Ihren Standort',
    greeting: 'Hallo {fullName},',
    'greeting-fallback': 'Hallo,',
    body:
      '{greeting}\n' +
      '\n' +
      'Sie wurden als Standortleitung für „{organisationName}" eingeladen. Schließen Sie das Onboarding Ihres Standorts über die Schaltfläche unten ab. Der Link ist 14 Tage gültig.',
    'cta-label': 'Onboarding starten',
    footnote: 'Wenn Sie diese Einladung nicht erwartet haben, können Sie diese E-Mail ignorieren.',
  },
  'partner-onboarding-submitted': {
    subject: 'Onboarding eingereicht: „{organizationName}"',
    title: 'Onboarding zur Prüfung eingereicht',
    greeting: 'Hallo {firstName},',
    'greeting-fallback': 'Hallo,',
    body:
      '{greeting}\n' +
      '\n' +
      'das Onboarding für den Standort „{organizationName}" wurde eingereicht und wartet auf Ihre Prüfung. Öffnen Sie immoOS über die Schaltfläche unten, um die Angaben zu prüfen.',
    'cta-label': 'Onboarding prüfen',
    footnote: 'Sie erhalten diese Benachrichtigung, weil Sie Owner oder Workspace-Admin dieses Workspaces sind.',
  },
  'onboarding-change-request': {
    subject: 'Änderungen erforderlich: Onboarding für „{organisationName}"',
    title: 'Bitte überarbeiten Sie Ihre Angaben',
    greeting: 'Hallo {fullName},',
    'greeting-fallback': 'Hallo,',
    body:
      '{greeting}\n' +
      '\n' +
      'Ihre Onboarding-Angaben für „{organisationName}" wurden geprüft und benötigen noch Anpassungen. Anmerkungen des Prüfers:\n' +
      '\n' +
      '{reviewNotes}\n' +
      '\n' +
      'Öffnen Sie das Onboarding über die Schaltfläche unten, um Ihre Angaben zu überarbeiten und erneut einzureichen.',
    'cta-label': 'Onboarding überarbeiten',
    footnote: 'Wenn Sie diese Nachricht nicht erwartet haben, können Sie diese E-Mail ignorieren.',
  },
  'partner-onboarding-approved': {
    subject: 'Ihr Standort „{organisationName}" wurde freigeschaltet',
    title: 'Standort freigeschaltet',
    greeting: 'Hallo {fullName},',
    'greeting-fallback': 'Hallo,',
    body:
      '{greeting}\n' +
      '\n' +
      'Ihre Onboarding-Angaben für „{organisationName}" wurden geprüft und freigegeben. Ihr Standort ist jetzt aktiv. Melden Sie sich über die Schaltfläche unten an, um loszulegen.',
    'cta-label': 'Anmelden',
    footnote: 'Wenn Sie diese Nachricht nicht erwartet haben, können Sie diese E-Mail ignorieren.',
  },
  'admin-invite-signup': {
    subject: 'Du wurdest als immoOS-Plattform-Admin hinzugefügt',
    title: 'Willkommen im immoOS-Adminbereich',
    greeting: 'Hallo {fullName},',
    'greeting-fallback': 'Hallo,',
    body:
      '{greeting}\n' +
      '\n' +
      'du wurdest als Plattform-Admin zu immoOS hinzugefügt. Richte dein Konto ein und nutze die Schaltfläche unten, um auf die Admin-Konsole zuzugreifen.',
    'cta-label': 'Konto einrichten',
    footnote: 'Wenn du diese Einladung nicht erwartet hast, kannst du diese E-Mail ignorieren.',
  },
  'admin-invite-signin': {
    subject: 'Du wurdest als immoOS-Plattform-Admin hinzugefügt',
    title: 'Willkommen im immoOS-Adminbereich',
    greeting: 'Hallo {fullName},',
    'greeting-fallback': 'Hallo,',
    body:
      '{greeting}\n' +
      '\n' +
      'du wurdest als Plattform-Admin zu immoOS hinzugefügt. Melde dich an, um auf die Admin-Konsole zuzugreifen.',
    'cta-label': 'Zur Admin-Konsole',
    footnote: 'Wenn du diese Einladung nicht erwartet hast, kannst du diese E-Mail ignorieren.',
  },
  'signing-invitation': {
    subject:
      '{companyName}: {documentType, select, BROKER_CONTRACT {Ihr Maklervertrag} other {Ihre Vollmacht}} zur Unterschrift',
    sent: '{agentName} von {companyName} hat Ihnen {documentType, select, BROKER_CONTRACT {den Maklervertrag} other {die Vollmacht}} für {propertyAddress} zur Unterschrift gesendet. Sie finden das Dokument als PDF im Anhang.',
    'sign-intro': 'Sie können es online unterschreiben, ohne es auszudrucken:',
    'link-label':
      '{documentType, select, BROKER_CONTRACT {Maklervertrag} other {Vollmacht}} ansehen und unterschreiben',
    personal: 'Der Link ist nur für Sie bestimmt. Bitte leiten Sie diese E-Mail nicht weiter.',
    questions: 'Bei Fragen erreichen Sie {agentName} unter {phone} oder per Antwort auf diese E-Mail.',
    closing: 'Mit freundlichen Grüßen',
  },
  'signing-reminder': {
    subject:
      'Erinnerung: {documentType, select, BROKER_CONTRACT {Ihr Maklervertrag wartet} other {Ihre Vollmacht wartet}} auf Ihre Unterschrift',
    pending:
      '{documentType, select, BROKER_CONTRACT {der Maklervertrag} other {die Vollmacht}} für {propertyAddress} von {companyName} ist noch nicht unterschrieben. Das Dokument liegt als PDF im Anhang.',
  },
  'signing-acceptance-reminder': {
    subject: 'Bitte bestätigen Sie Ihren Maklervertrag per E-Mail',
    intro:
      'vielen Dank für Ihre Unterschrift unter den Maklervertrag für {propertyAddress}. Bitte bestätigen Sie den Abschluss noch kurz, indem Sie auf diese E-Mail mit folgendem Text antworten:',
    'reply-link': 'Antwort mit diesem Text vorbereiten: {url}',
    closing: 'Mit freundlichen Grüßen',
  },
  'signing-confirmation': {
    subject:
      'Bestätigung: Sie haben {documentType, select, BROKER_CONTRACT {den Maklervertrag} other {die Vollmacht}} unterschrieben',
    signed:
      'Sie haben {documentType, select, BROKER_CONTRACT {den Maklervertrag} other {die Vollmacht}} für {propertyAddress} am {date} um {time} unterschrieben. Das Dokument liegt als PDF im Anhang.',
    belehrung: 'Die Widerrufsbelehrung und das Muster-Widerrufsformular sind Teil des angehängten PDFs.',
    'early-start-declared':
      'Sie haben dabei ausdrücklich verlangt, dass wir schon vor Ablauf der Widerrufsfrist mit der Maklertätigkeit beginnen, und bestätigt, dass Ihnen bekannt ist, dass Ihr Widerrufsrecht mit vollständiger Vertragserfüllung durch uns erlischt. Ihre Erklärung lautete wörtlich: „{declaration}“ (abgegeben am {date} um {time}).',
    'no-early-start': 'Wir beginnen mit der Maklertätigkeit erst nach Ablauf der Widerrufsfrist.',
    'acceptance-intro':
      'Bitte bestätigen Sie den Abschluss zusätzlich, indem Sie auf diese E-Mail mit folgendem Text antworten:',
    'acceptance-reply-link': 'Antwort mit diesem Text vorbereiten: {url}',
    'acceptance-reply-subject': 'Annahme Maklervertrag {propertyAddress}',
    'completion-follows':
      'Sobald alle Eigentümer unterschrieben haben, erhalten Sie das vollständig unterschriebene Dokument.',
    withdrawal:
      'Sie können den Vertrag bis zum Ablauf der Widerrufsfrist widerrufen, zum Beispiel per E-Mail an {email} oder online über die Schaltfläche „Vertrag widerrufen“: {link} Nutzen Sie die Online-Funktion, erhalten Sie unverzüglich eine Eingangsbestätigung mit dem Inhalt Ihrer Erklärung sowie Datum und Uhrzeit des Eingangs.',
    'withdrawal-online-only':
      'Sie können den Vertrag bis zum Ablauf der Widerrufsfrist widerrufen, zum Beispiel online über die Schaltfläche „Vertrag widerrufen“: {link} Nutzen Sie die Online-Funktion, erhalten Sie unverzüglich eine Eingangsbestätigung mit dem Inhalt Ihrer Erklärung sowie Datum und Uhrzeit des Eingangs.',
    closing: 'Mit freundlichen Grüßen',
  },
  'signing-completion': {
    subject: '{documentType, select, BROKER_CONTRACT {Maklervertrag} other {Vollmacht}} vollständig unterschrieben',
    body: 'alle Beteiligten haben {documentType, select, BROKER_CONTRACT {den Maklervertrag} other {die Vollmacht}} für {propertyAddress} unterschrieben. Das vollständig unterschriebene Dokument liegt als PDF im Anhang.',
  },
  'signing-completed': {
    subject: '{documentType, select, BROKER_CONTRACT {Maklervertrag} other {Vollmacht}} vollständig unterschrieben',
    greeting: 'Hallo {firstName},',
    'greeting-fallback': 'Hallo,',
    body: 'alle Beteiligten haben {documentType, select, BROKER_CONTRACT {den Maklervertrag} other {die Vollmacht}} für {propertyAddress} unterschrieben. Das vollständig unterschriebene Dokument liegt als PDF im Anhang.',
    'cta-label': 'Zum Deal',
  },
  'signing-signer-signed': {
    subject: '{name} hat unterschrieben',
    greeting: 'Hallo {firstName},',
    'greeting-fallback': 'Hallo,',
    body: '{name} hat {documentType, select, BROKER_CONTRACT {den Maklervertrag} other {die Vollmacht}} für {propertyAddress} unterschrieben. {signed} von {total} Eigentümern haben unterschrieben.',
    'cta-label': 'Zum Deal',
  },
  'signing-revocation-received': {
    subject: 'Widerruf: {name} hat den Maklervertrag widerrufen',
    greeting: 'Hallo {firstName},',
    'greeting-fallback': 'Hallo,',
    body: '{name} hat den Maklervertrag für {propertyAddress} am {date} um {time} widerrufen. Phase und Status des Deals bleiben unverändert. Prüfe, wie es mit dem Auftrag weitergeht.',
    'cta-label': 'Zum Deal',
  },
  'brokerage-notice': {
    subject: 'Ihr Maklervertrag für {propertyTitle}',
    greeting: {
      mr: 'Guten Tag Herr {lastName},',
      mrs: 'Guten Tag Frau {lastName},',
      person: 'Guten Tag {firstName} {lastName},',
      company: 'Guten Tag,',
    },
    interest: 'vielen Dank für Ihr Interesse an „{propertyTitle}“ in {propertyCity}.',
    commission:
      'Bevor wir Ihnen die Adresse und das vollständige Exposé geben dürfen, schließen Sie mit uns einen Maklervertrag ab. Für den Käufer fällt dabei folgende Provision an: {commission}',
    'link-intro': 'Unter diesem Link können Sie den Vertrag lesen und online abschließen:',
    'notice-intro':
      'Bitte lesen Sie vorher die folgende Widerrufsbelehrung. Sie ist auch auf der Seite hinter dem Link abrufbar.',
    questions: 'Bei Fragen antworten Sie einfach auf diese E-Mail.',
    closing: 'Mit freundlichen Grüßen',
  },
  'brokerage-reminder': {
    subject: 'Erinnerung: Ihr Maklervertrag für {propertyTitle}',
    reminder:
      'vor einigen Tagen haben wir Ihnen den Maklervertrag für „{propertyTitle}“ in {propertyCity} geschickt. Sobald Sie ihn abgeschlossen haben, können wir Ihnen Adresse und vollständiges Exposé geben.',
    'link-intro': 'Hier können Sie den Vertrag lesen und online abschließen:',
    'no-interest': 'Falls Sie kein Interesse mehr haben, müssen Sie nichts tun.',
  },
  'brokerage-confirmation': {
    subject: 'Bestätigung Ihres Maklervertrags für {propertyTitle}',
    concluded: 'Sie haben am {date} um {time} den folgenden Maklervertrag mit {companyName} abgeschlossen.',
    declarations: 'Ihre Erklärungen:',
    'declaration-read': '- Widerrufsbelehrung und Muster-Widerrufsformular gelesen: ja',
    'declaration-early-start':
      '- Vorzeitigen Beginn verlangt und Erlöschen des Widerrufsrechts zur Kenntnis genommen: {answer}',
    'declaration-verbatim': 'Ihre Erklärung lautete wörtlich: „{declaration}“ (abgegeben am {date} um {time}).',
    'acceptance-reply-intro':
      'Bitte bestätigen Sie den Abschluss zusätzlich, indem Sie auf diese E-Mail mit folgendem Text antworten:',
    'acceptance-reply-address': 'Ihre Antwort geht an {address}.',
    'acceptance-reply-link': 'Antwort mit diesem Text vorbereiten: {url}',
    'acceptance-reply-subject': 'Annahme Maklervertrag {contractNumber}',
    yes: 'ja',
    no: 'nein',
    'expose-download': 'Das vollständige Exposé können Sie hier herunterladen: {url}',
    'expose-soon': 'Das vollständige Exposé erhalten Sie in Kürze von uns.',
    'expose-not-requested': 'Da Sie keinen vorzeitigen Beginn verlangt haben, melden wir uns bei Ihnen.',
    'link-intro': 'Ihren Vertrag können Sie jederzeit hier einsehen und dort auch widerrufen:',
    closing: 'Mit freundlichen Grüßen',
  },
  'brokerage-acceptance-reminder': {
    subject: 'Bitte bestätigen Sie Ihren Maklervertrag per E-Mail',
    intro:
      'vielen Dank für Ihren Abschluss des Maklervertrags für „{propertyTitle}“. Bitte bestätigen Sie den Abschluss noch kurz, indem Sie auf diese E-Mail mit folgendem Text antworten:',
    closing: 'Mit freundlichen Grüßen',
  },
  'brokerage-agreement-concluded': {
    subject: 'Maklervertrag abgeschlossen: {buyerName} — {propertyTitle}',
    title: 'Maklervertrag abgeschlossen',
    greeting: 'Hallo {firstName},',
    'greeting-fallback': 'Hallo,',
    body: '{buyerName} hat den Maklervertrag für „{propertyTitle}“ am {date} um {time} online abgeschlossen.',
    'early-start-expose':
      'Vorzeitiger Beginn wurde verlangt — du kannst sofort tätig werden. Das Exposé wurde zum Download angeboten.',
    'early-start-no-expose':
      'Vorzeitiger Beginn wurde verlangt — du kannst sofort tätig werden. Am Objekt liegt noch keine Exposé-Vollversion; schick das Exposé bitte selbst.',
    'no-early-start':
      'Kein vorzeitiger Beginn verlangt. Die Widerrufsfrist endet am {date}; bis dahin bitte keine Leistungen wie Exposé oder Besichtigung.',
    'cta-label': 'Zum Interessenten',
  },
  'data-room-release': {
    subject: 'Unterlagen zu {propertyTitle}',
    released:
      'wir haben Ihnen die Unterlagen zu „{propertyTitle}“ in {propertyCity} freigegeben. Sie finden sie auf Ihrer persönlichen Seite, auf der Sie auch Ihren Maklervertrag einsehen können:',
    'released-no-city':
      'wir haben Ihnen die Unterlagen zu „{propertyTitle}“ freigegeben. Sie finden sie auf Ihrer persönlichen Seite, auf der Sie auch Ihren Maklervertrag einsehen können:',
    'released-without-contract':
      'wir haben Ihnen die Unterlagen zu „{propertyTitle}“ in {propertyCity} freigegeben. Sie finden sie auf Ihrer persönlichen Seite:',
    'released-without-contract-no-city':
      'wir haben Ihnen die Unterlagen zu „{propertyTitle}“ freigegeben. Sie finden sie auf Ihrer persönlichen Seite:',
    'how-to':
      'Dort können Sie jedes Dokument ansehen und herunterladen. Neue Unterlagen stellen wir Ihnen ebenfalls dort bereit.',
  },
  'brokerage-withdrawal-receipt': {
    subject: 'Eingangsbestätigung Ihres Widerrufs',
    received: 'Ihr Widerruf ist am {date} um {time} bei uns eingegangen. Sie haben Folgendes erklärt:',
    name: 'Name: {value}',
    contract: 'Vertrag: {value}',
    email: 'E-Mail-Adresse: {value}',
    reason: 'Grund: {value}',
    'reason-none': '—',
    closing: 'Mit freundlichen Grüßen',
  },
  'brokerage-withdrawal-received': {
    subject: 'Widerruf: {buyerName} — {propertyTitle}',
    title: 'Maklervertrag widerrufen',
    greeting: 'Hallo {firstName},',
    'greeting-fallback': 'Hallo,',
    body: '{buyerName} hat den Maklervertrag für „{propertyTitle}“ am {date} um {time} widerrufen.',
    reason: 'Grund: {reason}',
    'reason-none': 'keine Angabe',
    'moved-to-lost':
      'Der Interessent steht jetzt auf „Verloren“ mit dem Grund „Widerruf ausgeübt“. Bitte stelle deine Tätigkeit für diesen Interessenten ein.',
    'unchanged-lost':
      'Der Kaufprozess war bereits verloren und bleibt unverändert. Bitte prüfe, was der Widerruf für ihn bedeutet.',
    'unchanged-won':
      'Der Kaufprozess war bereits gewonnen und bleibt unverändert. Bitte prüfe, was der Widerruf für ihn bedeutet.',
    'cta-label': 'Zum Interessenten',
  },
  'new-inquiry': {
    subject: 'Neue Anfrage: {name} zu {propertyTitle} ({location})',
    'subject-no-location': 'Neue Anfrage: {name} zu {propertyTitle}',
    title: 'Neue Anfrage',
    greeting: 'Hallo {firstName},',
    'greeting-fallback': 'Hallo,',
    intro:
      'über {source} ist eine neue Anfrage zu deinem Objekt eingegangen. Wir haben {name} als Interessent in der Verkaufs-Pipeline angelegt.',
    'fallback-note': 'Du erhältst diese Anfrage, weil für das Objekt kein aktiver Verantwortlicher hinterlegt ist.',
    'group-prospect': 'INTERESSENT',
    'line-name': 'Name: {name}',
    'line-email': 'E-Mail: {email}',
    'line-phone': 'Telefon: {phone}',
    'group-message': 'NACHRICHT',
    'group-property': 'OBJEKT',
    'cta-label': 'Interessent öffnen',
    closing: 'Viele Grüße',
    signature: 'dein immoOS-Team',
    'source-label':
      '{source, select, WEBSITE {deine Website} WEB_EXPOSE {dein Web-Exposé} IMMOSCOUT24 {ImmoScout24} other {eine Quelle}}',
  },
  agency: {
    imprint: {
      phone: 'Telefon: {value}',
      email: 'E-Mail: {value}',
      'managing-directors': 'Geschäftsführung: {value}',
      register: 'Registergericht: {court} · Registernummer: {number}',
      'register-court-only': 'Registergericht: {court}',
      'register-number-only': 'Registernummer: {number}',
      'vat-id': 'USt-IdNr.: {value}',
      'supervisory-authority': 'Aufsichtsbehörde: {value}',
      'trade-permit-authority': 'Erlaubnisbehörde (§ 34c GewO): {value}',
      'professional-indemnity-insurance': 'Berufshaftpflicht: {value}',
    },
    'privacy-line': 'Datenschutz: {url}',
  },
  'viewing-invitation': {
    subject: 'Einladung zur Besichtigung: {propertyTitle} am {weekday}, {date}',
    heading: 'Ihre Besichtigung am {weekday}, {date}',
    'greeting-mr': 'Guten Tag Herr {lastName},',
    'greeting-mrs': 'Guten Tag Frau {lastName},',
    'greeting-name': 'Guten Tag {firstName} {lastName},',
    'greeting-neutral': 'Guten Tag,',
    intro:
      'wir freuen uns, Ihnen „{propertyTitle}" persönlich zeigen zu dürfen. Hier sind alle Details zu Ihrem Termin:',
    duration: '{minutes} Minuten',
    'when-all-day': '{weekday}, {date} · ganztägig',
    route: 'Route planen',
    'hint-label': 'Hinweis von {firstName}:',
    'hint-label-anonymous': 'Hinweis:',
    closing:
      'Den Termin finden Sie im Anhang — mit einem Klick ist er in Ihrem Kalender, und Sie können direkt zusagen. Passt der Termin nicht, antworten Sie einfach auf diese E-Mail, dann finden wir gemeinsam einen anderen.',
    'sign-off': 'Wir freuen uns auf Sie.',
    'calendar-summary': 'Besichtigung {subject}',
    'when-short': '{weekday}, {date} · {time} Uhr',
    rescheduled: {
      subject: 'Neuer Termin für Ihre Besichtigung: {propertyTitle}',
      heading: 'Ihre Besichtigung wurde verschoben',
      intro: 'Ihre Besichtigung von „{propertyTitle}" findet zu einem neuen Termin statt:',
      'new-label': 'Neu',
      'previous-label': 'Bisher:',
      closing:
        'Der Termin in Ihrem Kalender wird mit dem Anhang aktualisiert. Passt der neue Termin nicht, antworten Sie einfach auf diese E-Mail.',
    },
    cancelled: {
      subject: 'Abgesagt: Ihre Besichtigung von {propertyTitle} am {date}',
      heading: 'Ihre Besichtigung wurde abgesagt',
      intro:
        'leider müssen wir Ihre Besichtigung von „{propertyTitle}" am {weekday}, {date} um {time} Uhr absagen. Mit dem Anhang wird der Termin aus Ihrem Kalender entfernt.',
      'intro-all-day':
        'leider müssen wir Ihre Besichtigung von „{propertyTitle}" am {weekday}, {date} absagen. Mit dem Anhang wird der Termin aus Ihrem Kalender entfernt.',
      label: 'Abgesagt',
      closing: 'Möchten Sie einen neuen Termin? Antworten Sie einfach auf diese E-Mail oder rufen Sie uns an.',
    },
    reminder: {
      subject: 'Erinnerung: Ihre Besichtigung morgen um {time} Uhr — {propertyTitle}',
      'subject-all-day': 'Erinnerung: Ihre Besichtigung morgen — {propertyTitle}',
      heading: 'Morgen ist Ihre Besichtigung',
      intro: 'wir freuen uns auf morgen! Hier noch einmal alles Wichtige zu Ihrer Besichtigung von „{propertyTitle}":',
      closing: 'Kommt etwas dazwischen? Antworten Sie einfach auf diese E-Mail oder rufen Sie uns an.',
      'sign-off': 'Bis morgen!',
    },
  },
  buyer: {
    'contact-heading': 'Ihr Kontakt',
    call: 'Anrufen',
    privacy: 'Datenschutz: {url}',
    'managing-directors': 'Geschäftsführung: {value}',
    register: 'Registergericht: {court}, {number}',
    'register-court-only': 'Registergericht: {court}',
    'register-number-only': 'Registernummer: {number}',
    'vat-id': 'USt-IdNr.: {value}',
    'supervisory-authority': 'Aufsichtsbehörde: {value}',
    'trade-permit-authority': 'Erlaubnisbehörde nach § 34c GewO: {value}',
    'phone-email': 'Telefon {phone} · E-Mail {email}',
    'phone-only': 'Telefon {phone}',
    'email-only': 'E-Mail {email}',
  },
  'viewing-declined': {
    subject: 'Absage zur Besichtigung: {name} — {propertyTitle}',
    greeting: 'Hallo {firstName},',
    'greeting-fallback': 'Hallo,',
    body: '{name} hat die Besichtigung von „{propertyTitle}" am {weekday}, {date} um {time} Uhr abgelehnt.',
    'body-all-day': '{name} hat die Besichtigung von „{propertyTitle}" am {weekday}, {date} abgelehnt.',
    'next-step': 'Der Termin bleibt bestehen. Sag ihn ab oder vereinbare einen neuen.',
    link: 'Zum Interessenten: {url}',
    closing: 'Viele Grüße',
    signature: 'Dein immoOS-Team',
  },
} as const;
